import { ApiMethod } from '@wajeez-web/stores/base';
import { Server, contentClient } from '@wajeez-web/core/api-base';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';
import { PaginatedRequest } from '@wajeez-web/core/api';
import { thunkErrorHandler } from '@wajeez-web/core/utils';

export interface GetAllContentEntityTypes extends PaginatedRequest {
  contentItemType?: Server.ContentItemType;
  name?: string;
}

export const getAllContentEntityTypes: ApiMethod<
  GetAllContentEntityTypes,
  Server.ContentEntityTypeOutput[]
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntityTypes_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntityTypes_get_V1(
      undefined,
      data.contentItemType,
      data.name,
      data.page,
      data.pageSize
    );
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('getAllContentEntityTypes', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getContentEntityTypeLookups: ApiMethod<
  void,
  Server.ContentEntityTypeLookUpWithFieldsOutput[]
> = async (_, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntityTypes_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntityTypes_lookupsWithFields_get_V1();
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('getContentEntityTypeLookups', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export interface GetContentEntityTypeByNameVariables {
  name: string;
}

export const getContentEntityTypeByName: ApiMethod<
  GetContentEntityTypeByNameVariables,
  Server.ContentEntityTypeOutput
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntityTypes_Client(
      undefined,
      contentClient
    );
    const response = await client.contentEntityTypes_byType_get_V1(
      undefined,
      data.name
    );
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    console.log('getContentEntityTypeByName', error);
    // Show alert
    const errors = { ...error.response }.data;
    dispatch(
      CommonActions.setAlert({
        type: AlertType.WARNING,
        messages: JSON.parse(errors).errors || [
          { message: 'Something went wrong' },
        ],
      })
    );
    return rejectWithValue(error);
    throw error;
  }
};

export const getContentEntityTypeNavigations: ApiMethod<
  void,
  Server.DashboardContentEntityTypeNavigationOutput[],
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntityTypeNavigations_Client(
      undefined,
      contentClient
    );

    const response =
      await client.admin_ContentEntityTypeNavigations_lookups_get_V1();

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getSelectSortFactors: ApiMethod<
  void,
  Server.ContentEntitiesFactorsOutput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_ContentEntityTypes_Client(
      undefined,
      contentClient
    );

    const response =
      await client.admin_ContentEntityTypes_selectSortFactors_get_V1();

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const createContentEntityTypeMultilingual: ApiMethod<
  Server.CreateContentEntityTypeV2Input,
  number,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_ContentEntityTypes_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_ContentEntityTypes_post_V2(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const editContentEntityTypeMultilingual: ApiMethod<
  Server.UpdateContentEntityTypeInput,
  number,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_ContentEntityTypes_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_ContentEntityTypes_put_V1(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const getContentEntityTypeByIdMultilingual: ApiMethod<
  number,
  Server.ContentEntityTypeDetailedMultilingualOutput,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_ContentEntityTypes_Client(
        undefined,
        contentClient
      );

      const response =
        await client.admin_ContentEntityTypes_multilingualById_get_V1(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const getContentEntityTypesDatatable: ApiMethod<
  Server.ContentEntityTypeDatatable,
  Server.TableOfContentEntityTypeColumns,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_ContentEntityTypes_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_ContentEntityTypes_datatable_post_V1(
        data
      );

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};
